import clsx from 'clsx';
import { forwardRef, HTMLProps, PropsWithChildren, ReactNode } from 'react';
import { Stack } from '../../stack';
import classes from './card-header.module.scss';

export type CardHeaderProps = PropsWithChildren & {
  /**
   * Renders divider at the bottom of the header component
   */
  divider?: boolean;

  /**
   * Add custom prefix item before the title
   */
  prefix?: ReactNode;

  /**
   * Add custom suffix element on the right end of the title
   */
  suffix?: ReactNode;

  /**
   * Indicates whether an element should retain its position relative to its containing block
   * when scrolling occurs. Typically used for positioning elements in a fixed orientation
   * within a scrollable container.
   */
  sticky?: boolean;
} & Omit<HTMLProps<HTMLDivElement>, 'ref' | 'prefix'>;

/**
 * A functional React component that renders the header section of a card, allowing customization
 * through props like `className`, `divider`, `prefix`, and `suffix`. It forwards the ref to the root
 * HTMLDivElement and spreads additional props onto the root element. This component also applies
 * conditional styling when a divider is present.
 */
export const Header = forwardRef<
  HTMLDivElement,
  CardHeaderProps
>(({ children, className, divider, prefix, suffix, sticky, ...props }, ref) => {
  return (
    <div
      {...props}
      ref={ref}
      className={clsx(className, classes.root,
        'card-header-component',
        {
          [classes.withDivider]: divider,
          [classes.sticky]: sticky
        })}
    >
      <Stack align="spread" crossAxis="center">
        <Stack crossAxis="center">
          {prefix}
          <Stack direction={'column'} align="center" spacing={2}>
            {children}
          </Stack>
        </Stack>
        {suffix}
      </Stack>
    </div>
  );
});

Header.displayName = 'Card.Header';